// ---------------------------------------------------------------------------
// Bar chart — pure data → SVG string
// ---------------------------------------------------------------------------

import { svgText, svgAttr, scaleLinear, numberFormat, round2 } from './svg-util.js';
import { chartTitleDesc } from './chart-a11y.js';

/**
 * @typedef {Object} BarChartOpts
 * @property {string} idPrefix - unique id stem for aria targets.
 * @property {string} [title]
 * @property {string} [desc]
 * @property {number} [width]
 * @property {number} [height]
 */

/**
 * Render a vertical bar chart as an SVG string. Bars are evenly spaced; the
 * y-domain runs 0..max. Labels render under each bar; values above.
 *
 * @param {{ label: string, value: number }[]} bars
 * @param {BarChartOpts} opts
 * @returns {string}
 */
export function barChart(bars, opts) {
  const { idPrefix, title = 'Bar chart', desc = '', width = 320, height = 180 } = opts;
  const padX = 8;
  const padTop = 16;
  const padBottom = 28;
  const plotW = Math.max(0, width - padX * 2);
  const plotH = Math.max(0, height - padTop - padBottom);
  const safe = Array.isArray(bars) ? bars : [];
  const max = safe.reduce((m, b) => Math.max(m, Number(b?.value) || 0), 0);

  const { titleId, descId, svgDefs } = chartTitleDesc(
    title,
    desc || `Bar chart with ${safe.length} categories.`,
    idPrefix,
  );

  const n = safe.length;
  const slot = n > 0 ? plotW / n : 0;
  const barW = slot > 0 ? Math.max(1, slot * 0.6) : 0;

  const rects = safe
    .map((b, i) => {
      const value = Number(b?.value) || 0;
      const h = round2(scaleLinear(value, 0, max, 0, plotH));
      const x = round2(padX + i * slot + (slot - barW) / 2);
      const y = round2(padTop + (plotH - h));
      const cx = round2(x + barW / 2);
      const label = svgText(b?.label);
      const valLabel = svgText(numberFormat(value));
      return (
        `<rect class="chart-bar" x="${x}" y="${y}" width="${round2(barW)}" height="${h}"></rect>` +
        `<text class="chart-bar-value" x="${cx}" y="${round2(y - 4)}" text-anchor="middle">${valLabel}</text>` +
        `<text class="chart-bar-label" x="${cx}" y="${round2(height - 10)}" text-anchor="middle">${label}</text>`
      );
    })
    .join('');

  return (
    `<svg class="chart chart--bar" viewBox="0 0 ${svgAttr(width)} ${svgAttr(height)}" ` +
    `preserveAspectRatio="xMidYMid meet" aria-labelledby="${svgAttr(titleId)} ${svgAttr(descId)}">` +
    svgDefs +
    rects +
    `</svg>`
  );
}
